import { BuilderElement } from "@/types/builder";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getFromLocalStorage(key: string): string | null {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(key);
  }
  return null;
}

export function removeFromLocalStorage(key: string): void | null {
  if (typeof window !== 'undefined') {
    return window.localStorage.removeItem(key);
  }
  return null;
}

export function setToLocalStorage(key: string, value: string): void {
  if (typeof window !== 'undefined') {
    return window.localStorage.setItem(key, value);
  }
}

export function getFromSessionStorage(key: string): string | null {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key);
  }
  return null;
}

export function setToSessionStorage(key: string, value: string): void {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.setItem(key, value);
  }
}

type ToastOptions = {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'warning' | 'success';
};

type ToastFn = (options: ToastOptions) => void;

let toastImplementation: ToastFn | null = null;

export const registerToast = (fn: ToastFn) => {
  toastImplementation = fn;
};

export const showToast = (options: ToastOptions) => {
  if (toastImplementation) {
    toastImplementation(options);
  } else if (typeof window !== 'undefined') {
    console.warn('[toast] No toast implementation registered', options);
  }
};

// Helper: convert style object to CSS string
export function styleObjToCss(style: Record<string, any> = {}) {
  return Object.entries(style)
    .filter(([_, v]) => v != null && v !== '')
    .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}:${v};`)
    .join('');
}

export function getColorFilterPreview(color: string): string {
  // Parse the color to RGB
  let r = 0,
    g = 0,
    b = 0;

  // Handle rgb() colors like "rgb(249, 237, 50)"
  if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0], 10);
      g = parseInt(matches[1], 10);
      b = parseInt(matches[2], 10);
    }
  }
  // Handle hex colors like "#F9ED32"
  else if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  }

  // Normalize RGB values to 0-1
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  // Calculate brightness (luminance)
  const brightness = rNorm * 0.299 + gNorm * 0.587 + bNorm * 0.114;

  // Calculate saturation
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;
  const saturation = max === 0 ? 0 : delta / max;

  // Calculate hue
  let hue = 0;
  if (delta !== 0) {
    if (max === rNorm) {
      hue = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) * 60;
    } else if (max === gNorm) {
      hue = ((bNorm - rNorm) / delta + 2) * 60;
    } else {
      hue = ((rNorm - gNorm) / delta + 4) * 60;
    }
  }

  // Build filter string using a combination of filters
  const hueDeg = Math.round(hue);
  const filter = `brightness(0) saturate(100%) invert(${Math.round(
    brightness * 74
  )}%) sepia(${Math.round(saturation * 100)}%) saturate(${Math.round(
    saturation * 500
  )}%) hue-rotate(${hueDeg}deg) brightness(${Math.round(
    (brightness + 0.5) * 100
  )}%) contrast(${Math.round((brightness + 0.5) * 100)}%)`;

  return filter;
}

// Helper function to generate animation classes from Animate.css (Legacy support)
export const getAnimationClasses = (element: BuilderElement): string => {
  if (!element.animationEnabled || !element.animationName) {
    return '';
  }
  return `animate__animated animate__${element.animationName}`;
};

// New: Helper function to generate multiple animation classes
export const getMultiAnimationClasses = (element: BuilderElement): string => {
  const animations = element.animations || [];
  const enabledAnimations = animations.filter((anim) => anim.enabled);

  if (enabledAnimations.length === 0) {
    return '';
  }

  // Keep Animate.css base class and drive multiple keyframes via inline styles.
  return 'animate__animated';
};

// New: Helper function to generate inline styles for multiple animations
export const getMultiAnimationStyles = (
  element: BuilderElement
): React.CSSProperties => {
  const animations = element.animations || [];
  const enabledAnimations = animations.filter((anim) => anim.enabled && anim.name);

  if (enabledAnimations.length === 0) {
    return {};
  }

  const styles: React.CSSProperties = {};
  styles.animationName = enabledAnimations.map((anim) => anim.name).join(', ');
  styles.animationDuration = enabledAnimations
    .map((anim) => anim.duration || '1s')
    .join(', ');
  styles.animationDelay = enabledAnimations
    .map((anim) => anim.delay || '0s')
    .join(', ');
  styles.animationIterationCount = enabledAnimations
    .map((anim) => anim.iterationCount || '1')
    .join(', ');

  return styles;
};

export const getAnimationStyles = (element: BuilderElement): React.CSSProperties => {
  const styles: React.CSSProperties = {};

  if (element.animationEnabled) {
    if (element.animationDuration) {
      styles['--animate-duration' as any] = element.animationDuration;
    }
    if (element.animationDelay) {
      styles['--animate-delay' as any] = element.animationDelay;
    }
    if (element.animationIterationCount) {
      styles.animationIterationCount = element.animationIterationCount;
    }
  }

  return styles;
};